/**
 * src/compute/task-identity.ts
 * 
 * Domain-separated canonical hashing and deterministic identity derivation
 * for tasks, chunks, and execution attempts (Sprint DIST-02A.1).
 */

function getSubtleCrypto(): SubtleCrypto {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle;
  }
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto.subtle;
  }
  try {
    const nodeCrypto = require('node:crypto');
    return nodeCrypto.webcrypto.subtle;
  } catch {
    throw new Error('WebCrypto subtle is not available in current environment');
  }
}

/**
 * Deterministic JSON serialization with recursively sorted object keys.
 */
export function canonicalJson(value: any): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map(canonicalJson).join(',') + ']';
  }
  const keys = Object.keys(value).sort();
  const pairs = keys
    .filter((k) => value[k] !== undefined)
    .map((k) => JSON.stringify(k) + ':' + canonicalJson(value[k]));
  return '{' + pairs.join(',') + '}';
}

/**
 * Compute SHA-256 hex hash with domain separation prefix.
 */
export async function hashCanonical(domainPrefix: string, payload: any): Promise<string> {
  const subtle = getSubtleCrypto();
  const serialized = `${domainPrefix}:${canonicalJson(payload)}`;
  const buffer = new TextEncoder().encode(serialized);
  const hashBuffer = await subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Derives deterministic Task ID from logical request specification.
 */
export async function deriveTaskId(input: {
  meshId: string;
  workspaceId: string;
  taskClass: string;
  modelId: string;
  modelRevision: string;
  inputPayload: any;
  nonce?: string;
}): Promise<string> {
  const digest = await hashCanonical('herx/task-def/v1', input);
  return `task-${digest.slice(0, 16)}`;
}

/**
 * Derives deterministic Chunk ID.
 * Bound to taskId, chunkIndex, payloadHash, and chunking policyVersion.
 */
export async function deriveChunkId(input: {
  taskId: string;
  chunkIndex: number;
  payloadHash: string;
  policyVersion: string;
}): Promise<string> {
  const digest = await hashCanonical('herx/task-chunk/v1', input);
  return `chunk-${input.chunkIndex}-${digest.slice(0, 16)}`;
}

/**
 * Derives deterministic Attempt ID.
 * Distinguishes retry attempts from duplicate logical work.
 */
export async function deriveAttemptId(
  taskId: string,
  chunkId: string,
  attemptNumber: number
): Promise<string> {
  const digest = await hashCanonical('herx/task-attempt/v1', {
    taskId,
    chunkId,
    attemptNumber,
  });
  return `att-${attemptNumber}-${digest.slice(0, 12)}`;
}
